// @ts-nocheck -- baseline for pre-existing errors so typecheck can be turned on
// add the beginning of your app entry
import 'vite/modulepreload-polyfill';
import config from '@/services/config.service';
import { logError, setContextProvider } from '@/services/logger.service';
import Vue, { ref } from 'vue';
import 'vue-class-component/hooks'; // import hooks type to enable auto-complete
// ref: https://class-component.vuejs.org/guide/additional-hooks.html

import './styles/global.scss';
import App from '@/App.vue';
import router from '@/router';
import store, { loadPersistentState } from '@/store';
import { env } from '@/services/env.service';
import { switchTheme } from '@/services/theming.service';
import { get } from 'lodash-es';
import { isBrowser } from '@odin/utils';
import { deviceInfo, setupGlobalMixins, setupTheming, WsAutofocusDirective } from '@odin/vue2-utils';
import {
  setConfigValues,
  Bundler,
  setupGoogleAPI,
  setupMessages,
  setupTheming as setupThemingService
} from '@odin/services';
import { version as servicesVersion } from '@odin/services/package.json';
import { Screen } from 'quasar';

config.bundler = Bundler.rollup;

setConfigValues(config);

loadPersistentState();

import '@/utilities/filters';
import { cartApiServiceSetup, fetchCart } from '@/services/cart/cart-api.service';
import { changeAxiosConfig } from '@/services/network.service';
import { updateStateFromBackend } from '@/services/order.service';
import PortalVue from 'portal-vue';
import ResizeSensor from '@/vendor/resize-sensor/ResizeSensor.js';
import { hostingTutorialSetProducts } from '@/services/hosting-tutorial.service';
import { setupIwp } from '@/modules/iwp';
import { registerCartItemSettingsInDrawer, setupCheckout } from '@/modules/checkout';
import { setupAnalytics } from '@/services/analytics.service';
import { setupDeveloper } from '@/modules/developer';
import { setupI18n } from '@/services/i18n';
import { setupCrowdin } from '@/services/crowdin';
import { setupHotjar } from '@/services/hotjar';
import { setupPinia } from '@/services/pinia';
import { setupQuasar } from '@/services/quasar';
import { setupPortalOutlet, setupComponents } from '@odin/components-vue2';
import '@odin/components/dist/style.css';
import { registerProfilesSettingsInDrawer, setupProfiles } from '@/modules/profiles';
import { setupRecapitulationCartItems } from '@/modules/checkout';
import { setupPaymentMethod } from '@/modules/payment-method';
import { setupServiceSettings } from '@/modules/service-settings';
import { setupLoginSection } from '@/modules/login';
import {
  isInIframe,
  watchCartSize,
  notifyParentAboutClicks
} from '@/utilities/iframeCommunication';

let stopWatchingCartSize: (() => void) | null = null;
let stopNotifyingParentAboutClicks: (() => void) | null = null;


(window as any).ResizeSensor = ResizeSensor;

if (config.env === 'staging') {
  // must be before store loading to have store features in devtools work correctly
  Vue.config.devtools = true;
}

setupThemingService({ theme: config.theme, env: import.meta.env.MODE });
setupTheming(Vue, ref, { theme: config.theme, env: import.meta.env.MODE });

// Sentry
setContextProvider(
  () => ({
    cart: store.state.cart,
    ui: store.state.ui,
    bpsLength: get(store, 'state.billingProfiles.length', 0),
    dpsLength: get(store, 'state.domainProfiles.length', 0),
    login: get(store, 'state.cart.user.login', '_unknown_user_'),
  }),
  true
);

Vue.directive('ws-autofocus', WsAutofocusDirective);
Vue.use(PortalVue);
Vue.config.productionTip = false;

// Run VueAxe only in development and if enabled in .env
if (process.env.NODE_ENV === 'development' && env.VITE_SHOW_A11Y_ISSUES === 'true') {
  const { setupAccessibility } = await import('@/services/accessibility.service');
  setupAccessibility();
}

Vue.component(
  'WsSelect',
  () => import('@odin/components-vue2')
);


deviceInfo.init();

Vue.mixin(setupGlobalMixins(Screen, get));


// ===========>Services
changeAxiosConfig({ baseURL: config.apiUrl });
const pinia = setupPinia();
const i18nPromise = setupI18n();
setupPortalOutlet(Vue);
setupAnalytics();
setupCrowdin();
setupHotjar();

// @ts-ignore
if (window?.google) {
  // @ts-ignore
  setupGoogleAPI(window.google);
}


//===========>MODULES
setupIwp(router);
setupLoginSection();
setupCheckout(router);
setupServiceSettings(store);
setupPaymentMethod(store);
setupProfiles(store);
setupRecapitulationCartItems();
registerCartItemSettingsInDrawer();
registerProfilesSettingsInDrawer();
setupDeveloper(router);
cartApiServiceSetup();


const mountTarget = (config.appRootElm && document.querySelector(config.appRootElm)) || '#main-content';
const vueConfig = {
  router,
  store,
  // TODO: Fix TS error caused by Pinia: TS2769
  // @ts-ignore
  pinia,
  methods: {
    switchTheme,
    log: console.log,
  },

  beforeCreate() {
    // aligning breakpoints with SCSS ones
    // https://quasar.dev/options/screen-plugin#configuration
    this.$q.screen.setSizes({ md: 768, lg: 1260 });


    // initially load cart
    fetchCart();
    // Load hosting tutorial products into store
    hostingTutorialSetProducts();

    // automatically fetch cart changes on tab getting focus by user
    // solves scenario when user handles order in multiple tabs or devices
    if (isBrowser && env.VITE_DISABLE_AUTO_UPDATE_ON_REFOCUS === 'false') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          updateStateFromBackend();
        }
      });
    }
  },
  render: h => h(App),
  provide: {
    wsDev: Vue.prototype.$wsDev,
  },
};

i18nPromise.then(async (i18next) => {
  await setupQuasar();
  setupMessages({ translate: i18next.t, log: logError });
  setupComponents({ i18next, servicesVersion });

  const __WS_Cart__ = new Vue(vueConfig).$mount(mountTarget);

  // @ts-ignore
  window.__WS_Cart__ = __WS_Cart__;

  if (isInIframe()) {
    changeAxiosConfig({
      headers: {
        'AUTH-ID': config.userId,
      }
    });
    stopNotifyingParentAboutClicks?.();
    stopNotifyingParentAboutClicks = notifyParentAboutClicks();
    stopWatchingCartSize?.();
    stopWatchingCartSize = watchCartSize();
  }
});

// HMR dispose cleanup to prevent stacking observers
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    stopNotifyingParentAboutClicks?.();
    stopNotifyingParentAboutClicks = null;
    stopWatchingCartSize?.();
    stopWatchingCartSize = null;
  });
}
